import { useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuthStoreShallow } from "@/features/authentication";
import { cn } from "@/lib/cn";
import { useGetCurrentUserQuery, useGetFoldersQuery } from "@/shared/services";
import type { IStoriesFolder } from "@/shared/types";

interface IProps {
  setIsOpen: (status: boolean) => void;
  folderId: string;
  setFolder: (folder: { id: string; name: string }) => void;
}

type TFolderWithChildren = IStoriesFolder & {
  children: TFolderWithChildren[];
};

export const CreateStoryFoldersList = ({
  folderId,
  setIsOpen,
  setFolder,
}: IProps) => {
  const isAdmin = useAuthStoreShallow((s) => s.isAdminOrAdministrator);
  const { data: folders = [], isLoading: foldersLoading } =
    useGetFoldersQuery();
  const { data: user, isLoading: userLoading } = useGetCurrentUserQuery();

  const nestedFolders = useMemo(() => {
    if (!folders.length) return [];

    // Filter folders based on user permissions
    const userFolders = isAdmin
      ? folders
      : folders.filter(
          (folder) =>
            folder.users.some(
              (u: { oktaId: string }) => u.oktaId === user?.oktaId,
            ) || folder.openAccess,
        );

    // Build nested structure
    const buildNestedStructure = (
      allFolders: IStoriesFolder[],
      parentId: string | null | undefined,
    ): TFolderWithChildren[] => {
      return allFolders
        .filter((folder: IStoriesFolder) => {
          if (parentId === undefined) {
            return folder.parentId === null || folder.parentId === undefined;
          }
          return folder.parentId === parentId;
        })
        .map((folder: IStoriesFolder) => ({
          ...folder,
          children: buildNestedStructure(allFolders, folder._id),
        }));
    };

    return buildNestedStructure(userFolders, undefined);
  }, [folders, user, isAdmin]);

  if (foldersLoading || userLoading) return <Skeleton />;

  return (
    <ScrollArea maxHeight={248}>
      <div className="tw-p-2">
        <div className="tw-space-y-1">
          <Folder
            id={""}
            name={"Stories"}
            setIsOpen={setIsOpen}
            selectedFolderId={folderId}
            setFolder={setFolder}
          />
          {nestedFolders?.map((folder) => (
            <RecursiveFolder
              key={folder._id}
              folder={folder}
              setIsOpen={setIsOpen}
              selectedFolderId={folderId}
              setFolder={setFolder}
            />
          ))}
        </div>
      </div>
    </ScrollArea>
  );
};

interface IFolderProps {
  id: string;
  name: string;
  setIsOpen: (status: boolean) => void;
  selectedFolderId: string;
  setFolder: (folder: { id: string; name: string }) => void;
}

const Folder = ({
  setIsOpen,
  id,
  name,
  selectedFolderId,
  setFolder,
}: IFolderProps) => {
  const isActive = selectedFolderId === id;
  return (
    <div
      onClick={() => {
        setFolder({ id: id, name });
        setIsOpen(false);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setFolder({ id: id, name }); setIsOpen(false); } }}
      role="button"
      tabIndex={0}
      className={cn(
        "tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
        isActive && "tw-bg-blue-100",
      )}
    >
      <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
        {name}
      </span>
    </div>
  );
};

const RecursiveFolder = ({
  folder,
  level = 0,
  setIsOpen,
  selectedFolderId,
  setFolder,
}: {
  folder: TFolderWithChildren;
  level?: number;
  setIsOpen: (status: boolean) => void;
  selectedFolderId: string;
  setFolder: (folder: { id: string; name: string }) => void;
}) => {
  const isActive = selectedFolderId === folder._id;
  const paddingLeft = 36 + level * 16;

  return (
    <>
      <div
        className={cn(
          "tw-mt-1 tw-flex tw-h-9 tw-max-w-[448px] tw-cursor-pointer tw-items-center tw-rounded tw-pr-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
          isActive && "tw-bg-primary-50 tw-text-primary hover:tw-bg-primary-50",
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => {
          setFolder({ id: folder._id, name: folder.name });
          setIsOpen(false);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setFolder({ id: folder._id, name: folder.name }); setIsOpen(false); } }}
        role="button"
        tabIndex={0}
      >
        <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
          {folder.name}
        </span>
      </div>
      {folder.children?.map((childFolder) => (
        <RecursiveFolder
          key={childFolder._id}
          folder={childFolder}
          level={level + 1}
          setIsOpen={setIsOpen}
          selectedFolderId={selectedFolderId}
          setFolder={setFolder}
        />
      ))}
    </>
  );
};

const SKELETON_WIDTHS = [65, 72, 58, 78];

const Skeleton = () => {
  return (
    <div className="tw-w-[464px] tw-space-y-1 tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-p-2">
      {[1, 2, 3, 4].map((i) => (
        <div
          className="tw-flex tw-h-9 tw-animate-[pulse_.8s_linear_infinite] tw-items-center tw-px-3"
          key={i}
        >
          <span className="tw-mr-2 tw-block tw-size-4 tw-rounded-full tw-bg-skeleton-gradient"></span>
          <span
            className="tw-block tw-h-[6px] tw-rounded-xl tw-bg-skeleton-gradient"
            style={{ width: `${SKELETON_WIDTHS[i - 1]}%` }}
          ></span>
        </div>
      ))}
    </div>
  );
};
